import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const FLAGS: Record<string, string> = {
  pt: 'https://flagcdn.com/w40/br.png',
  en: 'https://flagcdn.com/w40/gb.png',
};

const LANG_LABELS: Record<string, string> = {
  pt: 'PT',
  en: 'EN',
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sobreOpen, setSobreOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [cartCount] = useState(0);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const currentLang = i18n.language?.startsWith('en') ? 'en' : 'pt';

  const switchLang = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
    setLangOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = () => setLangOpen(false);
    if (langOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [langOpen]);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-400 ${
        scrolled
          ? 'bg-[#0a0a0a] border-b border-[#c9a84c]/20'
          : 'bg-gradient-to-b from-black/80 to-transparent'
      }`}
    >
      <div className="px-6 md:px-10 flex items-center justify-between h-[72px]">
        {/* Logo */}
        <Link to="/" className="flex items-center flex-shrink-0">
          <img
            src="https://magadenidestilados.com/wp-content/uploads/2025/11/Magadeni-Final-Transparente.png"
            alt="Ma Gádeni Destilados"
            className="h-12 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-7">
          <Link to="/" className="text-white/70 hover:text-[#c9a84c] text-xs tracking-[0.18em] uppercase transition-colors whitespace-nowrap font-medium">
            {t('nav_home')}
          </Link>
          <Link to="/loja" className="text-white/70 hover:text-[#c9a84c] text-xs tracking-[0.18em] uppercase transition-colors whitespace-nowrap font-medium">
            {t('nav_shop')}
          </Link>
          <Link to="/whiskies" className="text-white/70 hover:text-[#c9a84c] text-xs tracking-[0.18em] uppercase transition-colors whitespace-nowrap font-medium">
            {t('nav_whiskies')}
          </Link>
          <Link to="/gins" className="text-white/70 hover:text-[#c9a84c] text-xs tracking-[0.18em] uppercase transition-colors whitespace-nowrap font-medium">
            {t('nav_gins')}
          </Link>

          {/* Sobre dropdown */}
          <div className="relative group">
            <button
              className="text-white/70 hover:text-[#c9a84c] text-xs tracking-[0.18em] uppercase transition-colors whitespace-nowrap font-medium flex items-center gap-1 cursor-pointer"
              onMouseEnter={() => setSobreOpen(true)}
              onMouseLeave={() => setSobreOpen(false)}
            >
              {t('nav_about')}
              <i className="ri-arrow-down-s-line text-xs"></i>
            </button>
            <div
              className={`absolute top-full left-0 mt-3 w-52 bg-[#0a0a0a] border border-[#c9a84c]/20 rounded-lg overflow-hidden transition-all duration-200 ${sobreOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}
              onMouseEnter={() => setSobreOpen(true)}
              onMouseLeave={() => setSobreOpen(false)}
            >
              <Link to="/our-story" className="block px-5 py-3 text-white/60 hover:text-[#c9a84c] hover:bg-white/5 text-xs tracking-widest uppercase transition-colors whitespace-nowrap">{t('nav_our_story')}</Link>
              <Link to="/the-estate" className="block px-5 py-3 text-white/60 hover:text-[#c9a84c] hover:bg-white/5 text-xs tracking-widest uppercase transition-colors whitespace-nowrap">{t('nav_islay')}</Link>
              <Link to="/speyside" className="block px-5 py-3 text-white/60 hover:text-[#c9a84c] hover:bg-white/5 text-xs tracking-widest uppercase transition-colors whitespace-nowrap">{t('nav_speyside')}</Link>
              <Link to="/descubra-as-highlands" className="block px-5 py-3 text-white/60 hover:text-[#c9a84c] hover:bg-white/5 text-xs tracking-widest uppercase transition-colors whitespace-nowrap">{t('nav_highlands')}</Link>
            </div>
          </div>

          <Link to="/contact" className="text-white/70 hover:text-[#c9a84c] text-xs tracking-[0.18em] uppercase transition-colors whitespace-nowrap font-medium">
            {t('nav_contact')}
          </Link>
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-3">
          {/* Language Switcher */}
          <div className="relative" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 border border-white/20 hover:border-[#c9a84c]/50 rounded-md px-2.5 py-1.5 text-white/70 hover:text-[#c9a84c] transition-all cursor-pointer whitespace-nowrap"
              aria-label="Switch language"
            >
              <img src={FLAGS[currentLang]} alt={currentLang} className="w-5 h-3.5 object-cover rounded-sm" />
              <span className="text-[10px] tracking-widest font-medium">{LANG_LABELS[currentLang]}</span>
              <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {langOpen && (
              <div className="absolute top-full right-0 mt-2 w-32 bg-[#0a0a0a] border border-[#c9a84c]/20 rounded-lg overflow-hidden z-50">
                {(['pt', 'en'] as const).map(lang => (
                  <button
                    key={lang}
                    onClick={() => switchLang(lang)}
                    className={`w-full flex items-center gap-2.5 px-4 py-3 text-xs tracking-widest uppercase transition-colors cursor-pointer whitespace-nowrap ${
                      currentLang === lang
                        ? 'text-[#c9a84c] bg-[#c9a84c]/10'
                        : 'text-white/60 hover:text-[#c9a84c] hover:bg-white/5'
                    }`}
                  >
                    <img src={FLAGS[lang]} alt={lang} className="w-5 h-3.5 object-cover rounded-sm" />
                    <span>{lang === 'pt' ? 'Português' : 'English'}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Cart */}
          <button
            onClick={() => navigate('/loja')}
            className="relative w-9 h-9 flex items-center justify-center text-white/70 hover:text-[#c9a84c] transition-colors cursor-pointer"
          >
            <i className="ri-shopping-bag-line text-xl"></i>
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#c9a84c] text-black text-[10px] font-bold rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>

          {/* CTA button desktop */}
          <Link
            to="/loja"
            className="hidden md:flex items-center gap-2 bg-[#c9a84c] hover:bg-[#b8963e] text-black text-xs tracking-widest uppercase font-bold px-5 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('nav_catalog')}
          </Link>

          {/* Mobile hamburger */}
          <button
            className="md:hidden w-9 h-9 flex items-center justify-center text-white cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-[#0a0a0a] border-t border-[#c9a84c]/20 px-6 py-5 flex flex-col gap-1">
          <Link to="/" className="text-white/70 text-xs tracking-widest uppercase py-3 border-b border-white/5" onClick={() => setMobileOpen(false)}>{t('nav_home')}</Link>
          <Link to="/loja" className="text-[#c9a84c] text-xs tracking-widest uppercase py-3 border-b border-white/5 font-medium" onClick={() => setMobileOpen(false)}>{t('nav_shop')}</Link>
          <Link to="/whiskies" className="text-white/70 text-xs tracking-widest uppercase py-3 border-b border-white/5" onClick={() => setMobileOpen(false)}>{t('nav_whiskies')}</Link>
          <Link to="/gins" className="text-white/70 text-xs tracking-widest uppercase py-3 border-b border-white/5" onClick={() => setMobileOpen(false)}>{t('nav_gins')}</Link>
          <Link to="/our-story" className="text-white/70 text-xs tracking-widest uppercase py-3 border-b border-white/5 pl-4" onClick={() => setMobileOpen(false)}>{t('nav_our_story')}</Link>
          <Link to="/the-estate" className="text-white/70 text-xs tracking-widest uppercase py-3 border-b border-white/5 pl-4" onClick={() => setMobileOpen(false)}>{t('nav_islay')}</Link>
          <Link to="/speyside" className="text-white/70 text-xs tracking-widest uppercase py-3 border-b border-white/5 pl-4" onClick={() => setMobileOpen(false)}>{t('nav_speyside')}</Link>
          <Link to="/descubra-as-highlands" className="text-white/70 text-xs tracking-widest uppercase py-3 border-b border-white/5 pl-4" onClick={() => setMobileOpen(false)}>{t('nav_highlands')}</Link>
          <Link to="/contact" className="text-white/70 text-xs tracking-widest uppercase py-3" onClick={() => setMobileOpen(false)}>{t('nav_contact')}</Link>
          <Link to="/loja" className="mt-3 bg-[#c9a84c] text-black text-xs tracking-widest uppercase font-bold px-5 py-3 rounded-md text-center whitespace-nowrap" onClick={() => setMobileOpen(false)}>{t('nav_catalog')}</Link>
        </div>
      )}
    </header>
  );
};

export default Navbar;
